import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Plus, Trash2, Send, Phone, Mail, Calendar, FileText, CheckSquare, Square } from "lucide-react";
import { toast } from "sonner";

const STAGES = ["lead", "contacted", "proposal", "won", "lost"];
const STAGE_COLORS = { lead: "secondary", contacted: "default", proposal: "default", won: "default", lost: "outline" };

export default function AdminCRM() {
  return (
    <div data-testid="admin-crm">
      <div className="border-b border-border pb-4 mb-6">
        <h1 className="font-display text-4xl tracking-tight">CRM</h1>
        <p className="text-sm text-muted-foreground italic mt-1">Contacts, deals, tasks and outreach.</p>
      </div>

      <Tabs defaultValue="contacts">
        <TabsList className="rounded-sm">
          <TabsTrigger value="contacts" data-testid="crm-tab-contacts">Contacts</TabsTrigger>
          <TabsTrigger value="deals" data-testid="crm-tab-deals">Deals</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="crm-tab-tasks">Tasks</TabsTrigger>
          <TabsTrigger value="campaigns" data-testid="crm-tab-campaigns">Campaigns</TabsTrigger>
        </TabsList>
        <TabsContent value="contacts" className="mt-6"><ContactsTab /></TabsContent>
        <TabsContent value="deals" className="mt-6"><DealsTab /></TabsContent>
        <TabsContent value="tasks" className="mt-6"><TasksTab /></TabsContent>
        <TabsContent value="campaigns" className="mt-6"><CampaignsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function ContactsTab() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = () => api.get("/admin/crm/contacts", { params: q ? { q } : {} }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [q]);

  const remove = async (cid) => {
    if (!window.confirm("Delete contact and all related deals/tasks/activity?")) return;
    await api.delete(`/admin/crm/contacts/${cid}`); load();
  };

  return (
    <>
      <div className="flex items-center justify-between gap-3 mb-4">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search contacts…" className="rounded-sm max-w-sm" data-testid="contacts-search" />
        <Button onClick={() => setEditing({})} className="rounded-sm" data-testid="new-contact"><Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />New contact</Button>
      </div>

      <div className="border border-border rounded-sm bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-[0.18em] text-muted-foreground border-b border-border">
            <tr><th className="px-4 py-3">Name</th><th>Email</th><th>Phone</th><th>Tags</th><th>Added</th><th></th></tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-muted-foreground italic">No contacts. Add your first.</td></tr>}
            {items.map((c) => (
              <tr key={c.id} className="border-b border-border last:border-0 cursor-pointer hover:bg-muted/40" onClick={() => setDetail(c.id)} data-testid={`contact-row-${c.id}`}>
                <td className="px-4 py-3 font-medium">{c.name}</td>
                <td>{c.email || "—"}</td>
                <td>{c.phone || "—"}</td>
                <td><div className="flex gap-1 flex-wrap">{(c.tags || []).map((t) => <Badge key={t} variant="outline" className="rounded-sm text-[10px]">{t}</Badge>)}</div></td>
                <td className="text-xs text-muted-foreground">{c.created_at?.slice(0, 10)}</td>
                <td className="text-right pr-4" onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="sm" onClick={() => setEditing(c)}>Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(c.id)} data-testid={`del-contact-${c.id}`}><Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <ContactDialog contact={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {detail && <ContactDetailDialog cid={detail} onClose={() => setDetail(null)} />}
    </>
  );
}

function ContactDialog({ contact, onClose, onSaved }) {
  const isNew = !contact;
  const [form, setForm] = useState({
    name: contact?.name || "", email: contact?.email || "", phone: contact?.phone || "",
    tags: (contact?.tags || []).join(", "), notes: contact?.notes || "",
  });

  const submit = async () => {
    const payload = { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) };
    try {
      if (isNew) await api.post("/admin/crm/contacts", payload);
      else await api.patch(`/admin/crm/contacts/${contact.id}`, payload);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-sm" data-testid="contact-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{isNew ? "New contact" : "Edit contact"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1.5" data-testid="ct-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm mt-1.5" data-testid="ct-email" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm mt-1.5" data-testid="ct-phone" /></div>
          </div>
          <div><Label>Tags (comma separated)</Label><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="vip, press, beta" className="rounded-sm mt-1.5" data-testid="ct-tags" /></div>
          <div><Label>Notes</Label><Textarea rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm mt-1.5" data-testid="ct-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} className="rounded-sm" data-testid="ct-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContactDetailDialog({ cid, onClose }) {
  const [data, setData] = useState(null);
  const [activity, setActivity] = useState({ type: "note", summary: "" });

  const load = () => api.get(`/admin/crm/contacts/${cid}`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [cid]);

  const logActivity = async () => {
    if (!activity.summary) return;
    await api.post("/admin/crm/activity", { contact_id: cid, ...activity });
    setActivity({ type: "note", summary: "" });
    load();
  };

  if (!data) return null;

  const ICONS = { call: Phone, email: Mail, meeting: Calendar, note: FileText };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-sm" data-testid="contact-detail">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl tracking-tight">{data.contact.name}</DialogTitle>
          <div className="text-sm text-muted-foreground space-y-0.5">
            {data.contact.email && <div className="flex items-center gap-2"><Mail className="h-3 w-3" strokeWidth={1.5} />{data.contact.email}</div>}
            {data.contact.phone && <div className="flex items-center gap-2"><Phone className="h-3 w-3" strokeWidth={1.5} />{data.contact.phone}</div>}
          </div>
        </DialogHeader>

        <div className="flex flex-wrap gap-1 mb-4">{(data.contact.tags || []).map((t) => <Badge key={t} variant="outline" className="rounded-sm">{t}</Badge>)}</div>

        {data.contact.notes && <div className="text-sm text-muted-foreground italic border-l-2 border-accent pl-3 my-3">{data.contact.notes}</div>}

        <div className="grid grid-cols-3 gap-3 my-4 text-center">
          <div className="border border-border rounded-sm p-3"><div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Deals</div><div className="text-2xl font-display mt-1">{data.deals.length}</div></div>
          <div className="border border-border rounded-sm p-3"><div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Open tasks</div><div className="text-2xl font-display mt-1">{data.tasks.filter((t) => !t.done).length}</div></div>
          <div className="border border-border rounded-sm p-3"><div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Orders</div><div className="text-2xl font-display mt-1">{data.orders.length}</div></div>
        </div>

        <div className="mb-5">
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Log activity</div>
          <div className="flex gap-2">
            <Select value={activity.type} onValueChange={(v) => setActivity({ ...activity, type: v })}>
              <SelectTrigger className="rounded-sm w-32" data-testid="act-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="note">Note</SelectItem>
                <SelectItem value="call">Call</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="meeting">Meeting</SelectItem>
              </SelectContent>
            </Select>
            <Input value={activity.summary} onChange={(e) => setActivity({ ...activity, summary: e.target.value })} placeholder="What happened?" className="flex-1 rounded-sm" data-testid="act-summary" />
            <Button onClick={logActivity} className="rounded-sm" data-testid="act-save">Log</Button>
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">Activity timeline</div>
          {data.activity.length === 0 ? <div className="text-sm text-muted-foreground italic">Nothing logged yet.</div> : (
            <div className="space-y-3">
              {data.activity.map((a) => {
                const Icon = ICONS[a.type] || FileText;
                return (
                  <div key={a.id} className="flex gap-3 text-sm border-b border-border pb-3" data-testid={`activity-${a.id}`}>
                    <div className="h-8 w-8 rounded-sm bg-muted flex items-center justify-center text-accent shrink-0"><Icon className="h-3.5 w-3.5" strokeWidth={1.5} /></div>
                    <div className="flex-1">
                      <div className="text-sm">{a.summary}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{a.by_user_name} · {a.created_at?.slice(0, 10)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DealsTab() {
  const [items, setItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/crm/deals").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/admin/crm/contacts").then((r) => setContacts(r.data)); }, []);

  const remove = async (did) => { await api.delete(`/admin/crm/deals/${did}`); load(); };
  const updateStage = async (did, stage) => { await api.patch(`/admin/crm/deals/${did}`, { stage }); load(); };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-muted-foreground italic">Track opportunities through your pipeline.</div>
        <Button onClick={() => setEditing({})} className="rounded-sm" data-testid="new-deal"><Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />New deal</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {STAGES.map((stage) => {
          const stageItems = items.filter((d) => d.stage === stage);
          const stageValue = stageItems.reduce((s, d) => s + (d.value || 0), 0);
          return (
            <div key={stage} className="border border-border rounded-sm bg-card flex flex-col">
              <div className="px-3 py-2.5 border-b border-border">
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{stage}</div>
                <div className="text-sm font-medium">{stageItems.length} · A${stageValue.toFixed(0)}</div>
              </div>
              <div className="p-2 space-y-2 min-h-[200px]">
                {stageItems.map((d) => (
                  <div key={d.id} className="border border-border/60 rounded-sm p-2.5 text-sm bg-background hover:border-accent/60 cursor-pointer" onClick={() => setEditing(d)} data-testid={`deal-${d.id}`}>
                    <div className="font-display text-base leading-tight">{d.title}</div>
                    {d.contact_name && <div className="text-xs text-muted-foreground mt-0.5">{d.contact_name}</div>}
                    <div className="text-xs font-semibold mt-1.5">A${(d.value || 0).toFixed(2)}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {editing && <DealDialog deal={editing.id ? editing : null} contacts={contacts}
        onDelete={editing.id ? () => { remove(editing.id); setEditing(null); } : null}
        onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </>
  );
}

function DealDialog({ deal, contacts, onClose, onSaved, onDelete }) {
  const isNew = !deal;
  const [form, setForm] = useState({
    title: deal?.title || "", contact_id: deal?.contact_id || "",
    value: deal?.value || 0, stage: deal?.stage || "lead", notes: deal?.notes || "",
  });
  const submit = async () => {
    const payload = { ...form, value: parseFloat(form.value) || 0, contact_id: form.contact_id || null };
    if (isNew) await api.post("/admin/crm/deals", payload);
    else await api.patch(`/admin/crm/deals/${deal.id}`, payload);
    toast.success("Saved"); onSaved();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm" data-testid="deal-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{isNew ? "New deal" : "Edit deal"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-sm mt-1.5" data-testid="d-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Value ($)</Label><Input type="number" step="0.01" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="rounded-sm mt-1.5" data-testid="d-value" /></div>
            <div>
              <Label>Stage</Label>
              <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="d-stage"><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Contact</Label>
            <Select value={form.contact_id || "none"} onValueChange={(v) => setForm({ ...form, contact_id: v === "none" ? "" : v })}>
              <SelectTrigger className="rounded-sm mt-1.5" data-testid="d-contact"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None —</SelectItem>
                {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Notes</Label><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm mt-1.5" /></div>
        </div>
        <DialogFooter className="flex items-center justify-between">
          {onDelete ? <Button variant="ghost" onClick={onDelete} className="text-destructive">Delete</Button> : <div />}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} className="rounded-sm">Cancel</Button>
            <Button onClick={submit} className="rounded-sm" data-testid="d-save">Save</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TasksTab() {
  const [items, setItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("open");

  const load = () => api.get("/admin/crm/tasks").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/admin/crm/contacts").then((r) => setContacts(r.data)); }, []);

  const toggleDone = async (t) => { await api.patch(`/admin/crm/tasks/${t.id}`, { done: !t.done }); load(); };
  const remove = async (tid) => { await api.delete(`/admin/crm/tasks/${tid}`); load(); };

  const filtered = items.filter((t) => filter === "all" || (filter === "open" ? !t.done : t.done));

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-1">
          {["open", "done", "all"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} className={`text-xs uppercase tracking-[0.18em] px-3 py-1.5 rounded-sm ${filter === f ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`} data-testid={`tasks-filter-${f}`}>{f}</button>
          ))}
        </div>
        <Button onClick={() => setEditing({})} className="rounded-sm" data-testid="new-task"><Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />New task</Button>
      </div>

      <div className="border border-border rounded-sm bg-card divide-y divide-border">
        {filtered.length === 0 && <div className="p-6 text-sm text-muted-foreground italic text-center">No tasks.</div>}
        {filtered.map((t) => (
          <div key={t.id} className="flex items-start gap-3 px-4 py-3" data-testid={`task-${t.id}`}>
            <button onClick={() => toggleDone(t)} className="mt-0.5 text-accent" data-testid={`toggle-${t.id}`}>
              {t.done ? <CheckSquare className="h-4 w-4" strokeWidth={1.5} /> : <Square className="h-4 w-4" strokeWidth={1.5} />}
            </button>
            <div className="flex-1">
              <div className={`text-sm ${t.done ? "line-through text-muted-foreground" : ""}`}>{t.title}</div>
              <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-3">
                {t.due_date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" strokeWidth={1.5} />{t.due_date.slice(0, 10)}</span>}
                {t.contact_name && <span>· {t.contact_name}</span>}
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setEditing(t)}>Edit</Button>
            <Button variant="ghost" size="sm" onClick={() => remove(t.id)}><Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} /></Button>
          </div>
        ))}
      </div>

      {editing && <TaskDialog task={editing.id ? editing : null} contacts={contacts} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </>
  );
}

function TaskDialog({ task, contacts, onClose, onSaved }) {
  const isNew = !task;
  const [form, setForm] = useState({
    title: task?.title || "", contact_id: task?.contact_id || "",
    due_date: task?.due_date?.slice(0, 10) || "", notes: task?.notes || "", done: task?.done || false,
  });
  const submit = async () => {
    const payload = { ...form, contact_id: form.contact_id || null, due_date: form.due_date || null };
    if (isNew) await api.post("/admin/crm/tasks", payload);
    else await api.patch(`/admin/crm/tasks/${task.id}`, payload);
    toast.success("Saved"); onSaved();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm" data-testid="task-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{isNew ? "New task" : "Edit task"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-sm mt-1.5" data-testid="t-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rounded-sm mt-1.5" data-testid="t-due" /></div>
            <div>
              <Label>Contact</Label>
              <Select value={form.contact_id || "none"} onValueChange={(v) => setForm({ ...form, contact_id: v === "none" ? "" : v })}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="t-contact"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Notes</Label><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm mt-1.5" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} className="rounded-sm" data-testid="t-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CampaignsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/crm/campaigns").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const send = async (cid) => {
    if (!window.confirm("Send this campaign now? This is MOCKED — emails are logged, not delivered.")) return;
    try {
      const { data } = await api.post(`/admin/crm/campaigns/${cid}/send`);
      toast.success(`MOCK: Sent to ${data.sent_count} recipients`);
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const remove = async (cid) => { await api.delete(`/admin/crm/campaigns/${cid}`); load(); };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-sm text-muted-foreground italic">Broadcast to your customers or CRM contacts.</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-accent mt-1">Email sending is MOCKED (logged to server)</div>
        </div>
        <Button onClick={() => setEditing({})} className="rounded-sm" data-testid="new-campaign"><Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />New campaign</Button>
      </div>

      <div className="space-y-3">
        {items.length === 0 && <div className="border border-dashed border-border rounded-sm p-10 text-sm text-muted-foreground italic text-center">No campaigns yet.</div>}
        {items.map((c) => (
          <div key={c.id} className="border border-border rounded-sm p-4 bg-card flex items-start justify-between gap-4" data-testid={`camp-${c.id}`}>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <div className="font-display text-lg">{c.name}</div>
                <Badge variant={c.status === "sent" ? "default" : "secondary"} className="rounded-sm capitalize">{c.status}</Badge>
                {c.sent_count > 0 && <Badge variant="outline" className="rounded-sm">{c.sent_count} sent</Badge>}
              </div>
              <div className="text-sm text-muted-foreground mt-1"><strong>Subject:</strong> {c.subject}</div>
              <div className="text-xs text-muted-foreground mt-1 italic">Audience: {c.audience}</div>
              <div className="text-sm mt-2 text-muted-foreground line-clamp-2 whitespace-pre-line">{c.body}</div>
            </div>
            <div className="flex flex-col gap-1">
              {c.status !== "sent" && <Button onClick={() => send(c.id)} className="rounded-sm" size="sm" data-testid={`send-${c.id}`}><Send className="h-3.5 w-3.5 mr-2" strokeWidth={1.5} />Send</Button>}
              <Button variant="ghost" size="sm" onClick={() => remove(c.id)}><Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} /></Button>
            </div>
          </div>
        ))}
      </div>

      {editing && <CampaignDialog onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </>
  );
}

function CampaignDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", subject: "", body: "", audience: "all_customers" });
  const submit = async () => {
    if (!form.name || !form.subject) { toast.error("Name + subject required"); return; }
    await api.post("/admin/crm/campaigns", form);
    toast.success("Draft saved"); onSaved();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-xl" data-testid="campaign-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">New campaign</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Internal name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1.5" data-testid="camp-name" /></div>
          <div><Label>Subject line</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="rounded-sm mt-1.5" data-testid="camp-subject" /></div>
          <div>
            <Label>Audience</Label>
            <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
              <SelectTrigger className="rounded-sm mt-1.5" data-testid="camp-audience"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all_customers">All customers (purchased)</SelectItem>
                <SelectItem value="all_contacts">All CRM contacts</SelectItem>
                <SelectItem value="tag:newsletter">Tag: Between The Lines (newsletter)</SelectItem>
                <SelectItem value="tag:customer">Tag: Customers</SelectItem>
                <SelectItem value="tag:vip">Tag: VIP</SelectItem>
                <SelectItem value="tag:press">Tag: Press</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Body</Label><Textarea rows={8} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="rounded-sm mt-1.5" data-testid="camp-body" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} className="rounded-sm" data-testid="camp-save">Save draft</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
